import React, { useState, useEffect } from 'react';
import { RefreshCw, CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';
import './OrderList.scss';

interface Order {
  id: number;
  user_id: number;
  service_id: number;
  job_id: number | null;
  duration_days: number;
  price_paid: number;
  currency: string;
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  payment_method: string;
  payment_reference: string | null;
  starts_at: string | null;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
  service_name: string;
  service_type: string;
  user_email: string;
  user_name: string;
  job_title: string | null;
}

const OrderList: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterService, setFilterService] = useState<string>('all');

  const API_URL = '/api';

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/services/admin/purchases`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch orders');
      }

      const result = await response.json();
      if (result.success) {
        setOrders(result.data);
      } else {
        throw new Error(result.error || 'Failed to fetch orders');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateOrderStatus = async (orderId: number, newStatus: string) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/services/admin/purchases/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        fetchOrders(); // Refresh the list
      } else {
        alert('Nepavyko atnaujinti užsakymo būsenos');
      }
    } catch (err) {
      console.error('Error updating order status:', err);
      alert('Klaida atnaujinant užsakymo būseną');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <span className="status-badge completed"><CheckCircle size={14} /> Įvykdytas</span>;
      case 'pending':
        return <span className="status-badge pending"><Clock size={14} /> Laukiama</span>;
      case 'failed':
        return <span className="status-badge failed"><XCircle size={14} /> Nepavyko</span>;
      case 'refunded':
        return <span className="status-badge refunded"><AlertCircle size={14} /> Grąžintas</span>;
      default:
        return <span className="status-badge">{status}</span>;
    }
  };

  const getServiceTypeName = (serviceType: string) => {
    switch (serviceType) {
      case 'cv_database_access':
        return 'CV prieiga';
      case 'job_promotion':
        return 'Skelbimo iškėlimas';
      case 'additional_job_slot':
        return 'Papildoma vieta';
      default:
        return serviceType;
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('lt-LT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredOrders = orders.filter(order => {
    if (filterStatus !== 'all' && order.status !== filterStatus) return false;
    if (filterService !== 'all' && order.service_type !== filterService) return false;
    return true;
  });

  // Calculate stats
  const stats = {
    total: orders.length,
    completed: orders.filter(o => o.status === 'completed').length,
    pending: orders.filter(o => o.status === 'pending').length,
    totalRevenue: orders
      .filter(o => o.status === 'completed')
      .reduce((sum, o) => sum + Number(o.price_paid), 0)
  };

  if (loading) {
    return (
      <div className="orders-page">
        <div className="loading">Kraunama...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="orders-page">
        <div className="error">Klaida: {error}</div>
      </div>
    );
  }

  return (
    <div className="orders-page">
      <div className="page-header">
        <div className="header-left">
          <h1>Užsakymai</h1>
          <p className="subtitle">Paslaugų pirkimo istorija</p>
        </div>
        <button className="btn-refresh" onClick={fetchOrders}>
          <RefreshCw size={16} />
          Atnaujinti
        </button>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Viso užsakymų</div>
        </div>
        <div className="stat-card completed">
          <div className="stat-value">{stats.completed}</div>
          <div className="stat-label">Įvykdyta</div>
        </div>
        <div className="stat-card pending">
          <div className="stat-value">{stats.pending}</div>
          <div className="stat-label">Laukiama</div>
        </div>
        <div className="stat-card revenue">
          <div className="stat-value">{stats.totalRevenue.toFixed(2)} €</div>
          <div className="stat-label">Pajamos</div>
        </div>
      </div>

      <div className="filters-row">
        <div className="filter-group">
          <label>Būsena:</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">Visos</option>
            <option value="completed">Įvykdyti</option>
            <option value="pending">Laukiama</option>
            <option value="failed">Nepavykę</option>
            <option value="refunded">Grąžinti</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Paslauga:</label>
          <select value={filterService} onChange={(e) => setFilterService(e.target.value)}>
            <option value="all">Visos</option>
            <option value="cv_database_access">CV prieiga</option>
            <option value="job_promotion">Skelbimo iškėlimas</option>
            <option value="additional_job_slot">Papildoma vieta</option>
          </select>
        </div>
        <div className="results-count">
          Rodoma: {filteredOrders.length} iš {orders.length}
        </div>
      </div>

      <div className="orders-table-container">
        <table className="orders-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Data</th>
              <th>Klientas</th>
              <th>Paslauga</th>
              <th>Trukmė</th>
              <th>Kaina</th>
              <th>Būsena</th>
              <th>Galioja iki</th>
              <th>Veiksmai</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.length === 0 ? (
              <tr>
                <td colSpan={9} className="empty-message">
                  Užsakymų nerasta
                </td>
              </tr>
            ) : (
              filteredOrders.map((order) => (
                <tr key={order.id}>
                  <td className="id-cell">#{order.id}</td>
                  <td className="date-cell">{formatDate(order.created_at)}</td>
                  <td className="client-cell">
                    <div className="client-name">{order.user_name}</div>
                    <div className="client-email">{order.user_email}</div>
                  </td>
                  <td className="service-cell">
                    <div className="service-name">{order.service_name}</div>
                    <div className="service-type">{getServiceTypeName(order.service_type)}</div>
                    {order.job_title && (
                      <div className="job-title">Skelbimas: {order.job_title}</div>
                    )}
                  </td>
                  <td className="duration-cell">
                    {order.duration_days > 0 ? `${order.duration_days} d.` : 'Vienkartinis'}
                  </td>
                  <td className="price-cell">
                    {Number(order.price_paid).toFixed(2)} {order.currency}
                  </td>
                  <td className="status-cell">
                    {getStatusBadge(order.status)}
                  </td>
                  <td className="expires-cell">
                    {order.expires_at ? formatDate(order.expires_at) : '-'}
                  </td>
                  <td className="actions-cell">
                    <select 
                      value={order.status}
                      onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                      className="status-select"
                    >
                      <option value="pending">Laukiama</option>
                      <option value="completed">Įvykdytas</option>
                      <option value="failed">Nepavyko</option>
                      <option value="refunded">Grąžintas</option>
                    </select>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OrderList;

